import React, { useCallback, useEffect, useRef, useState } from "react"
import API from "../../api"
import { ROUTES } from "../../routes"
import { uniqueId } from "lodash"
import dayjs from "../../plugins/dayjs"
import { ChevronRight, ArrowLeft } from "react-feather"

// redux
import { mapDispatchProps, mapStateToProps } from "../../store/store"
import { connect } from "react-redux"
import { useQueryParams } from "../../hooks/useQueryParams"
import { useLocation, useNavigate } from "react-router-dom"

import useInfiniteScroll from "../../hooks/useInfiniteScroll"

import { debounce } from "../../helpers/debounce"
import { isEmpty } from "../../helpers/object"

// components
import Button from "../../components/button"
import Checkbox, { ICON_TYPES } from "../../components/checkbox"
import Divider from "../../components/divider"
import InputDate from "../../components/inputDate"
import Paginator from "../../components/paginator"
import PostingCard from "../../components/postingCard"
import Select from "../../components/select"
import TextInputField from "../../components/textInput"
import NotificationContainer from "../../components/notification/index"
import ModalPostingImagesDownloader from "./modalPostingImagesDownloader"
import BRAND from "../../constants/brand"
import { APP_LOCALE, useLanguage } from "@languages/index"
import { LOCALES } from "constants/locales"
import ModalRequestRecruitingCampaignApplicationBrand from "components/modalBrandRequestRecruitingCampaignApplication"

const PostingIndex = (props: any) => {
  const IS_DOWNLOADING_POSTINGS_ENABLED = false;

  const isWholesale = props.brand?.businessType === BRAND.BUSINESS_TYPE.WHOLESALE
  const isBeautyCategoryBrand = props.brand?.isCategoryBeauty

  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.pages.postings, ...language.pages.campaigns }
  const navigate = useNavigate()

  const FILTER_TYPES = {
    ALL: "ALL",
    RENTAL: "RENTAL",
    SEEDING: "SEEDING",
  }

  const [postings, setPostings] = useState<any>()
  const [postingStatusDuration, setPostingStatusDuration] = useState({
    startDt: dayjs().startOf("month").format("YYYY-MM-DD"),
    endDt: dayjs().endOf("month").format("YYYY-MM-DD"),
  })
  const [postingStatus, setPostingStatus] = useState({
    rentalOngoingModelPostingCount: 0,
    rentalOngoingAmbassasorPostingCount: 0,
    seedingOngoingPostingCount: 0,
    rentalCompletedModelPostingCount: 0,
    rentalCompletedAmbassasorPostingCount: 0,
    seedingCompletedPostingCount: 0,
  })

  const [paginator, setPaginator] = useState<any>()
  const [isBrandFiltered, setIsBrandFiltered] = useState(true)
  const [isTypeFiltered, setIsTypeFiltered] = useState(FILTER_TYPES.ALL)
  const [searchText, setSearchText] = useState("")
  const durationTypeOptions =
    process.env.LOCALE == LOCALES.KO
      ? [
          { name: languageTexts.index_duration_option_1, value: "rental" },
          { name: languageTexts.index_duration_option_2, value: "posting" },
        ]
      : [{ name: languageTexts.index_duration_option_2, value: "posting" }]

  const durations = [
    {
      name: languageTexts.index_duration_1,
      startDt: "",
      endDt: "",
    },
    {
      name: languageTexts.index_duration_2,
      startDt: dayjs().subtract(90, "day").format("YYYY-MM-DD"),
      endDt: dayjs().format("YYYY-MM-DD"),
    },
    {
      name: languageTexts.index_duration_3,
      startDt: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
      endDt: dayjs().format("YYYY-MM-DD"),
    },
    {
      name: languageTexts.index_duration_4,
      startDt: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
      endDt: dayjs().format("YYYY-MM-DD"),
    },
    {
      name: languageTexts.index_duration_5,
      startDt: dayjs().startOf("month").format("YYYY-MM-DD"),
      endDt: dayjs().endOf("month").format("YYYY-MM-DD"),
    },
    {
      name: languageTexts.index_duration_6,
      startDt: dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
      endDt: dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
    },
    {
      name: languageTexts.index_duration_7,
      startDt: dayjs().startOf("week").format("YYYY-MM-DD"),
      endDt: dayjs().endOf("week").format("YYYY-MM-DD"),
    },
    {
      name: languageTexts.index_duration_8,
      startDt: dayjs().subtract(1, "week").startOf("week").format("YYYY-MM-DD"),
      endDt: dayjs().subtract(1, "week").endOf("week").format("YYYY-MM-DD"),
    },
  ]

  const [selectedDurationTypeOption, setSelectedDurationTypeOption] = useState(durationTypeOptions[0])
  const [currentDuration, setCurrentDuration] = useState(durations[0])
  const location = useLocation()

  const searchParams = useRef<any>(location.state)
  const [currentPage, setCurrentPage] = useState(1)
  const postingsCount = useRef(0)

  const [modalPostingImagesDownloaderIsVisible, setModalPostingImagesDownloaderIsVisible] = useState(false)

  const handlePostingStatus = async (diffMonth, refresh?) => {
    if (diffMonth === 0) return
    let hasNoNextMonth = diffMonth > 0 && !dayjs().isAfter(dayjs(postingStatusDuration.endDt), "month")
    if (hasNoNextMonth) return

    let curSdt = dayjs(postingStatusDuration.startDt)
    let targetDt = diffMonth < 0 ? curSdt.subtract(-diffMonth, "month") : curSdt.add(diffMonth, "month")

    setPostingStatusDuration({
      startDt: targetDt.startOf("month").format("YYYY-MM-DD"),
      endDt: targetDt.endOf("month").format("YYYY-MM-DD"),
    })
  }

  const removeSearchParamsKeys = (keys) => {
    if (searchParams.current == null) return
    for (let key of keys) {
      searchParams.current[key] = null
      delete searchParams.current[key]
      switch (key) {
        case "searchText":
          setSearchText("")
          break
        default:
          break
      }
    }
  }

  const fetchPostingStatus = async () => {
    await API.Brand.getPostingStatus({
      brand_token: props.auth.token,
      brand_id: props.brand.id,
      ...postingStatusDuration,
    })
      .then((response) => {
        const posting_status = response.data.postingStatus
        setPostingStatus({
          ...posting_status,
        })
      })
      .catch((error) => {
        props.showModal({ text: error.message })
      })
  }

  const makeFetchPostingsParamsPre = () => {
    let params = {
      brandToken: props.auth.token,
      brandId: props.brand.id,
      type: isTypeFiltered !== "ALL" ? isTypeFiltered : null,
      durationType: selectedDurationTypeOption.value,
      ...currentDuration,
    }

    if (searchParams.current != null && Object.keys(searchParams.current).length > 0) {
      Object.assign(params, searchParams.current)
    }

    if (searchText != null && searchText.trim().length > 0) params["searchText"] = searchText

    return params
  }

  const fetchPostings = async (config?) => {
    let targetPage = config?.page ? config.page : currentPage
    if (config?.refresh) targetPage = 1

    let params = makeFetchPostingsParamsPre()
    params["page"] = targetPage
    params["brandId"] = isBrandFiltered ? props.brand.id : null
    params["with"] = ["downloadable_videos", "user.suggestions", "rental.guidelines"]
    params["append"] = ["validated_partnership_code"]
    params["derivedData"] = ["exposed_video_url"]

    setPostings([])
    await API.Post.index(params)
      .then((response) => {
        const _paginator = response.data.paginator
        postingsCount.current = response.data.count

        setPostings(() => {
          return response.data.postings
        })

        setCurrentPage(targetPage)
        setIsContentEnded(_paginator.currentPage >= _paginator.lastPage)
        setPaginator(_paginator)
        setIsFetching(false)
      })
      .catch((error) => {
        props.showModal({ text: error.message })
      })
  }

  useEffect(() => {
    if (APP_LOCALE === LOCALES.KO) {
      fetchPostingStatus()
    }
    fetchPostings()
  }, [])

  useEffect(() => {
    fetchPostingStatus()
  }, [postingStatusDuration])

  useEffect(() => {
    setIsContentEnded(false)
    if (postings) {
      fetchPostings({ refresh: true })
    }
  }, [isBrandFiltered])

  useEffect(() => {
    setIsContentEnded(false)
    if (postings) {
      fetchPostings({ refresh: true })
    }
  }, [isTypeFiltered])

  //// INFINITY SCROLL
  // const [isFetching, setIsFetching, isContentEnded, setIsContentEnded] =
  //   useInfiniteScroll(async () => {
  //     // if (postings && postings.length) {
  //     // await fetchPostings();
  //     // }
  //   });
  const [isFetching, setIsFetching] = useState(true)
  const [isContentEnded, setIsContentEnded] = useState(false)

  function tryParseDayJs(dateStr) {
    const date = dayjs(dateStr, "YYYY-MM-DD", true)
    if (date.toDate().toString() === "Invalid Date") return null
    return date
  }

  const onSubmit = () => {
    if (isFetching) return
    if (
      (currentDuration?.startDt == null || currentDuration.startDt === "") &&
      currentDuration?.endDt != null &&
      currentDuration.endDt !== ""
    ) {
      props.showModal({ text: languageTexts.index_text_1 })
      return
    }

    setIsFetching(true)
    setIsContentEnded(false)
    if (searchText != null) {
      if (searchText.trim().length > 0) {
        if (searchParams.current == null) searchParams.current = {}
        searchParams.current["hasSubSearch"] = true
        searchParams.current["search"] = 2

        removeSearchParamsKeys(["productMasterId"])
        if (searchParams.current["userId"] == null && searchParams.current["productMasterId"] == null)
          removeSearchParamsKeys(["name"])
      } else {
        if (searchParams.current != null) {
          if (searchParams.current["userId"] == null && searchParams.current["productMasterId"] == null)
            delete searchParams.current["hasSubSearch"]
          delete searchParams.current["search"]
        }
      }
    }
    fetchPostings({ refresh: true })
  }

  const renderLoadingIndicator = () => {
    return isFetching && isContentEnded == false ? (
      <div className="flex flex-row mx-10 my-10 justify-center items-center">
        <div className="flex flex-row justify-center text-center my-5">
          <span className="prose prose-xl text-slate-400">{languageTexts.loading_1}</span>
        </div>
      </div>
    ) : null
  }

  const renderPostingHeaderStatus = () => {
    if (props.brand.isAmbassadorServiceEnabled == false) return null

    let st = dayjs(postingStatusDuration.startDt)
    let month = st.get("month") + 1

    let rent_completed_count =
      postingStatus["rentalCompletedModelPostingCount"] + postingStatus["rentalCompletedAmbassasorPostingCount"]
    let rent_ongoing_count =
      postingStatus["rentalOngoingModelPostingCount"] + postingStatus["rentalOngoingAmbassasorPostingCount"]

    return (
      <div>
        <div className="flex flex-col px-5 lg:px-8 xl:px-0 mx-auto md:w-[800px] xl:w-[1024px] mt-8 gap-2">
          <div className="hidden md:block basis-full md:basis-3/12 text-4xl font-bold">{languageTexts.posting}</div>
          <div className="basis-full md:basis-9/12 lg:basis-6/12 text-[15px] mt-5 md:mt-2">
            <div className="flex flex-row justify-center items-center text-[22px]">
              <div
                className="cursor-pointer"
                onClick={() => {
                  handlePostingStatus(-1)
                }}
              >
                <img
                  className={"w-[22px] h-[22px] stroke-[2px] mx-1"}
                  src={`/assets/icons/ic-arrow-left-black-circle.svg`}
                />
              </div>
              <div className="ml-5 font-bold">{languageTexts._index_text_2(st.get("year"), st.get("month") + 1)}</div>
              <div
                className="cursor-pointer ml-5"
                onClick={() => {
                  handlePostingStatus(1)
                }}
              >
                <img
                  className={"w-[22px] h-[22px] stroke-[2px] mx-1"}
                  src={`/assets/icons/ic-arrow-right-black-circle.svg`}
                />
              </div>
            </div>

            {!isWholesale && !isBeautyCategoryBrand && (
              <>
                <div className="flex flex-row justify-between mt-[38px]">
                  <div>{languageTexts._index_text_3(month)}</div>
                  <div className="font-bold text-[#000000]">{languageTexts._amount(rent_completed_count)}</div>
                </div>
                <div className="mt-1 text-end text-[13px] text-[#999999]">
                  {languageTexts._index_text_4(
                    postingStatus["rentalCompletedModelPostingCount"],
                    postingStatus["rentalCompletedAmbassasorPostingCount"]
                  )}
                </div>
                <div className="flex flex-row justify-between mt-[15px]">
                  <div>{languageTexts._index_text_5(month)}</div>
                  <div className="font-bold text-[#000000]">{languageTexts._amount(rent_ongoing_count)}</div>
                </div>
                <div className="mt-1 text-end text-[13px] text-[#999999]">
                  {languageTexts._index_text_4(
                    postingStatus["rentalOngoingModelPostingCount"],
                    postingStatus["rentalOngoingAmbassasorPostingCount"]
                  )}
                </div>
                <Divider style={{ borderColor: "rgba(0,0,0,0.1)" }} className="mt-[15px]" />
              </>
            )}

            <div className="flex flex-row justify-between mt-[15px]">
              <div>{languageTexts._index_text_6(month)}</div>
              <div className="font-bold text-[#000000]">
                {languageTexts._amount(postingStatus["seedingCompletedPostingCount"])}
              </div>
            </div>
            <div className="flex flex-row justify-between mt-[15px]">
              <div>{languageTexts._index_text_7(month)}</div>
              <div className="font-bold text-[#000000]">
                {languageTexts._amount(postingStatus["seedingOngoingPostingCount"])}
              </div>
            </div>
          </div>
          <div className="basis-3/12 hidden lg:block"></div>
        </div>
      </div>
    )
  }

  const renderModalPostingImagesDownloaderOpener = () => {
    return (
      <div className="flex flex-row justify-end">
        <div
          onClick={(e) => {
            if (postingsCount.current === 0) {
              props.showModal({ text: languageTexts.index_text_12 })
              return
            }
            if (postingsCount.current > 50) {
              props.showModal({
                text: languageTexts.index_text_13,
              })
              return
            }
            setModalPostingImagesDownloaderIsVisible(true)
          }}
          className="cursor-pointer underline underline-offset-2 text-base"
        >
          {languageTexts.index_text_14}
        </div>
      </div>
    )
  }

  const renderSearchPanel = () => {
    const [startDt, setStartDt] = useState(durations[0].startDt)
    const [endDt, setEndDt] = useState(durations[0].endDt)

    return (
      <div className="flex flex-col px-5 lg:px-8 xl:px-0 mx-auto md:w-[800px] xl:w-[1024px] mt-5 mb-4 gap-4">
        <div>
          <TextInputField
            className="py-0 h-full"
            inputClassName="flex justify-center px-3 py-3 h-full text-left text-sm tracking-tighter"
            bordered
            value={searchText}
            onChangeText={(value) => {
              if (isFetching) return
              setSearchText(value)
            }}
            onSubmit={onSubmit}
            placeholder={languageTexts.product_name}
          />
        </div>
        <div className="flex flex-row basis-full items-start gap-3 flex-wrap">
          <div className="min-w-[100px] w-full md:w-auto">
            <Select
              selectClassName={"text-sm py-3 px-2 pr-4"}
              bordered
              margin={0}
              options={durationTypeOptions}
              value={selectedDurationTypeOption.name}
              selected={selectedDurationTypeOption}
              onChange={(value) => {
                const selected = durationTypeOptions.filter((option) => option.name == value)[0]
                setSelectedDurationTypeOption(selected)
              }}
            />
          </div>
          <div className="flex flex-row flex-1 basis-full md:basis-0 gap-2 md:w-full justify-between items-start">
            <InputDate
              placeholder={languageTexts.start_date}
              className="w-full h-[40px]"
              value={startDt}
              onChange={(value) => {
                const currentEndDt = tryParseDayJs(currentDuration["endDt"])
                if (currentEndDt != null) {
                  if (dayjs(value).isAfter(currentEndDt)) {
                    props.showModal({
                      text: languageTexts.index_text_9,
                    })
                    return false
                  }
                }
                setStartDt(value)
                setCurrentDuration({
                  name: "",
                  startDt: value,
                  endDt: endDt,
                })
                return true
              }}
            />

            <div className="h-full justify-center self-baseline">
              <span className="tracking-tighter leading-[40px]">{` ~ `}</span>
            </div>

            <InputDate
              placeholder={languageTexts.end_date}
              className="w-full h-[40px] whitespace-nowrap"
              value={endDt}
              onChange={(value) => {
                const currentStartDt = tryParseDayJs(currentDuration["startDt"])
                if (currentStartDt != null) {
                  if (dayjs(value).isBefore(currentStartDt)) {
                    props.showModal({
                      text: languageTexts.index_text_10,
                    })
                    return false
                  }
                }
                setEndDt(value)
                setCurrentDuration({
                  name: "",
                  startDt: startDt,
                  endDt: value,
                })
                return true
              }}
            />
            <Button
              className={"flex px-5 py-3 items-center justify-center select-none"}
              text={languageTexts.index_text_11}
              textClassName={"font-sm"}
              buttonColor={"BLACK"}
              textColor={"WHITE"}
              justify={"center"}
              onClick={onSubmit}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {durations.map((duration, i) => {
            return (
              <Button
                key={i}
                className={"flex px-3 py-2 items-center"}
                textClassName={"text-sm tracking-tighter text-center mx-auto word-keep"}
                text={`${duration.name}`}
                bordered
                borderColor={currentDuration.name == duration.name ? "BLACK" : null}
                textColor={"BLACK"}
                justify={"center"}
                onClick={() => {
                  setStartDt(duration.startDt)
                  setEndDt(duration.endDt)
                  setCurrentDuration(duration)
                }}
              />
            )
          })}
        </div>

        {/* 포스팅 일괄 다운로드 기능 : IS_DOWNLOADING_POSTINGS_ENABLED로 ON/OFF */}
        {(
          !IS_DOWNLOADING_POSTINGS_ENABLED || 
          (!isBrandFiltered || postingsCount.current === 0 || isFetching)
        ) ? null : (
          renderModalPostingImagesDownloaderOpener()
        )}
      </div>
    )
  }

  const getTitleName = () => {
    // PostingCard의 메뉴
    if (searchParams.current && searchParams.current.userId) {
      return (
        <div className="flex flex-row">
          <div
            className="cursor-pointer"
            onClick={() => {
              if (searchParams.current != null) removeSearchParamsKeys(["name", "userId"])
              if (searchText == null || searchText.trim().length == 0) removeSearchParamsKeys(["hasSubSearch"])
              fetchPostings({ refresh: true })
            }}
          >
            <ArrowLeft style={{ strokeWidth: 3 }} />
          </div>
          <div className="ml-10px">
            <div>
              <span className="font-bold text-[28px] tracking-tighter whitespace-nowrap">{`${searchParams.current.name}`}</span>
              <span className="text-[28px] tracking-tighter whitespace-nowrap">{languageTexts.index_text_15}</span>
            </div>
            {searchText == null || searchText.trim().length === 0 ? null : (
              <div className="mt-10px font-bold text-[16px] tracking-tighter whitespace-nowrap">
                <span>{languageTexts._index_text_16(searchText.trim())}</span>
              </div>
            )}
          </div>
        </div>
      )
    }
    if (searchParams.current && searchParams.current.productMasterId)
      return (
        <>
          <div
            className="cursor-pointer"
            onClick={() => {
              if (searchParams.current != null) removeSearchParamsKeys(["name", "productMasterId"])
              if (searchText == null || searchText.trim().length == 0) removeSearchParamsKeys(["hasSubSearch"])
              fetchPostings({ refresh: true })
            }}
          >
            <ArrowLeft style={{ strokeWidth: 3 }} />
          </div>
          <span className="ml-10px font-bold text-[28px] tracking-tighter whitespace-nowrap	">{`${searchParams.current.name}`}</span>
          <span className="text-[28px] tracking-tighter whitespace-nowrap	">{languageTexts.index_text_17}</span>
        </>
      )

    // 전체 상단 제품명 검색
    if (searchText != null && searchText.trim().length > 0)
      return (
        <>
          <div
            className="cursor-pointer"
            onClick={() => {
              if (
                searchParams.current != null &&
                searchParams.current["userId"] == null &&
                searchParams.current["productMasterId"] == null
              )
                removeSearchParamsKeys(["hasSubSearch"])
              setSearchText("")
              fetchPostings({ refresh: true })
            }}
          >
            <ArrowLeft style={{ strokeWidth: 3 }} />
          </div>
          <span className="ml-10px font-bold text-[28px] tracking-tighter whitespace-nowrap	">
            {languageTexts._index_text_18(searchText.trim())}
          </span>
        </>
      )

    return (
      <>
        <span className="font-bold text-xl tracking-tighter">{languageTexts.index_text_19}</span>
      </>
    )
  }

  const renderPostings = useCallback(() => {
    const checkboxLabelStyle = {
      letterSpacing: "-0.05em",
      color: "#999999",
    }

    return (
      <div className="flex flex-col px-5 lg:px-8 xl:px-0 mx-auto md:w-[800px] xl:w-[1024px] mt-[32px]">
        <div className="flex flex-row mb-6 items-center flex-wrap">{getTitleName()}</div>
        <div className="flex flex-col md:flex-row mb-6">
          <div className="flex flex-col gap-1">
            <div className=" flex flex-row">
              <span className="font-bold tracking-tighter text-base">
                {languageTexts._index_text_20(postingsCount.current ? postingsCount.current.toLocaleString() : 0)}
              </span>
            </div>
          </div>
          <div className="flex flex-col md:ml-auto">
            <div
              className="flex flex-row md:ml-auto items-center opacity-50 cursor-pointer"
              onClick={() => {
                navigate(ROUTES.SETTINGS_BRAND_ALARM.PATH)
              }}
            >
              <span className="text-sm tracking-tighter">{languageTexts.index_text_29(props.brand.categoryId)}</span>
              <ChevronRight size={16} />
            </div>

            {isBeautyCategoryBrand ||
            (searchParams.current &&
              !isEmpty(searchParams.current) &&
              searchParams.current.hasSubSearch &&
              (searchParams.current.userId || searchParams.current.productMasterId)) ? null : (
              <div className="flex flex-col md:flex-row md:ml-auto md:items-center cursor-pointer text-black gap-1">
                <div className="flex flex-row gap-6 mr-6 my-3">
                  <Checkbox
                    className="text-sm"
                    textStyle={checkboxLabelStyle}
                    text={languageTexts.index_text_23}
                    checked={isTypeFiltered == FILTER_TYPES.ALL}
                    type={ICON_TYPES.CIRCLE}
                    onClick={() => {
                      setIsTypeFiltered(FILTER_TYPES.ALL)
                    }}
                  />

                  <Checkbox
                    className="text-sm"
                    textStyle={checkboxLabelStyle}
                    text={languageTexts.index_text_24}
                    checked={isTypeFiltered == FILTER_TYPES.SEEDING}
                    type={ICON_TYPES.CIRCLE}
                    onClick={() => {
                      setIsTypeFiltered(FILTER_TYPES.SEEDING)
                    }}
                  />

                  {process.env.LOCALE == LOCALES.KO ? (
                    <Checkbox
                      className="text-sm"
                      textStyle={checkboxLabelStyle}
                      text={languageTexts.index_text_25}
                      checked={isTypeFiltered == FILTER_TYPES.RENTAL}
                      type={ICON_TYPES.CIRCLE}
                      onClick={() => {
                        setIsTypeFiltered(FILTER_TYPES.RENTAL)
                      }}
                    />
                  ) : null}
                </div>

                {/* <Checkbox
                  className="text-sm"
                  textStyle={checkboxLabelStyle}
                  text={languageTexts.index_text_26}
                  checked={isBrandFiltered ? false : true}
                  onClick={() => {
                    setIsBrandFiltered(!isBrandFiltered);
                  }}
                /> */}
              </div>
            )}
          </div>
        </div>

        {postings && postings.length > 0 && (
          <div className="flex flex-col gap-[64px] mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-y-[64px] gap-x-[32px] xl:gap-y-10">
              {postings.map((posting: any) => {
                return (
                  <PostingCard
                    key={`posting-${posting.id}`}
                    posting={posting}
                    showFeedbacks={true}
                    onUpdate={(posting?: any) => {
                      const postingsCopy = [...postings].map((postingOriginal) =>
                        postingOriginal.id === posting.id ? posting : postingOriginal
                      )

                      setPostings(postingsCopy)
                    }}
                    onLike={(userId: number, result: any) => {
                      const postingsCopy = [...postings].map((postingOriginal) =>
                        postingOriginal.userId === userId
                          ? {
                              ...postingOriginal,
                              ...{
                                likes: result ? [true] : [],
                              },
                            }
                          : postingOriginal
                      )

                      setPostings(postingsCopy)
                    }}
                    onSearch={(params: any) => {
                      if (searchParams.current == null) searchParams.current = {}
                      let keys_to_remove = ["name", "userId", "productMasterId"]
                      if (params != null && params["productMasterId"] != null) setSearchText("")
                      removeSearchParamsKeys(keys_to_remove)
                      Object.assign(searchParams.current, params)
                      searchParams.current["hasSubSearch"]
                      fetchPostings({ refresh: true })
                    }}
                  />
                )
              })}
            </div>

            {paginator && (
              <Paginator
                currentPage={currentPage}
                lastPage={paginator.lastPage}
                onPageChange={(page) => {
                  fetchPostings({ page: page })
                }}
              />
            )}
          </div>
        )}
      </div>
    )
  }, [postings, isBrandFiltered, paginator, currentPage])

  useEffect(() => {
    return () => {
      props.setNotification({ isVisible: false })
    }
  }, [location, props.preferences.isMobile])

  return (
    <>
      {APP_LOCALE === LOCALES.KO && renderPostingHeaderStatus()}
      {APP_LOCALE === LOCALES.KO && renderSearchPanel()}
      {renderPostings()}
      {renderLoadingIndicator()}
      <NotificationContainer />
      <ModalPostingImagesDownloader
        isVisible={modalPostingImagesDownloaderIsVisible}
        letClose={() => setModalPostingImagesDownloaderIsVisible(false)}
        makeFetchParams={makeFetchPostingsParamsPre}
      />
    </>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(PostingIndex)
